import React, {
  useEffect,
  useState,
  SyntheticEvent,
  Dispatch,
  SetStateAction,
} from 'react';
import {
  Box,
  Divider,
  Typography,
  Grid,
  Paper,
} from '@mui/material';
import { CreditCard as PaymentsIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { AxiosError } from 'axios';
import { useSelector } from 'react-redux';
import Header from '../../../components/Layout/Header';
import CustomTable from '../../../components/General/CustomTable';
import EmptyTable from '../../../components/General/EmptyTable';
import { getIndicators } from '../../../requests/api/contractPayment';
import handleApiResponse from '../../../utils/handleApiResponse';
import { formatCcy } from '../../../utils/helpers';
import {
  CONTRACT_PAYMENTS_UI,
  ContractWithUserData,
} from '../types';
import { ReduxState } from '../../../types';
import { SECRETARY } from '../../../utils/user_types';
import { getLocations } from '../../../requests/api/sections';
import { LocationsData } from '../../Section/SectionListView/types';
import useStyles from './styles';

function BaseContractPaymentListView({
  type,
  title,
  allContracts,
  tableParameters,
  searchValue,
  filterPaid,
  filterLocation,
  setSearchValue,
  setFilterPaid,
  setFilterLocation,
  setTableParameters,
  selectedYear,
  indicatorsRefreshKey = 0,
}: {
  type: 'fee' | 'tuition',
  title: string,
  allContracts: {
    items: ContractWithUserData[],
    totalItems: number,
  },
  tableParameters: { page: number, rowsPerPage: number },
  searchValue: string,
  filterPaid: string,
  filterLocation: string,
  setSearchValue: Dispatch<SetStateAction<string>>,
  setFilterPaid: Dispatch<SetStateAction<string>>,
  setFilterLocation: Dispatch<SetStateAction<string>>,
  setTableParameters: Dispatch<SetStateAction<{ page: number, rowsPerPage: number }>>,
  selectedYear: number,
  indicatorsRefreshKey?: number,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const account = useSelector((state: ReduxState) => state.account);
  const [locationsInfo, setLocationsInfo] = useState<LocationsData[]>([]);
  const [summaryData, setSummaryData] = useState<{
    total: number,
    paidTotal: number,
    escuelaMilitar: number,
    paidEscuelaMilitar: number,
    losDominicos: number,
    paidLosDominicos: number,
    laDehesa: number,
    paidLaDehesa: number,
    egresadosEscuelaMilitar: number,
    paidEgresadosEscuelaMilitar: number,
  }>({
    total: 0,
    paidTotal: 0,
    escuelaMilitar: 0,
    paidEscuelaMilitar: 0,
    losDominicos: 0,
    paidLosDominicos: 0,
    laDehesa: 0,
    paidLaDehesa: 0,
    egresadosEscuelaMilitar: 0,
    paidEgresadosEscuelaMilitar: 0,
  });

  useEffect(() => {
    const fetchLocationsInfo = async () => {
      try {
        const { locations } = await getLocations();

        if (!Array.isArray(locations)) {
          throw new Error('Formato inesperado en la respuesta de getLocations');
        }

        const filtered = locations.filter((loc) => loc.name !== 'Online');

        const result = [{ id: '', name: 'Todas' }, ...filtered];

        const escuelaMilitar = filtered.find((loc) => loc.name === 'Escuela Militar');
        if (escuelaMilitar) {
          result.push({
            id: `${escuelaMilitar.id}-Egresados`,
            name: 'Egresados Escuela Militar',
          });
        }

        setLocationsInfo(result);
      } catch (err) {
        handleApiResponse(enqueueSnackbar, err as AxiosError, false);
      }
    };

    fetchLocationsInfo();
  }, []);

  useEffect(() => {
    const fetchIndicatorsData = async () => {
      try {
        const { indicators } = await getIndicators(type as 'fee' | 'tuition', selectedYear);

        setSummaryData(indicators);
      } catch (err) {
        handleApiResponse(enqueueSnackbar, err as AxiosError, false);
      }
    };

    fetchIndicatorsData();
  }, [selectedYear, indicatorsRefreshKey]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handlePaidFilterChange = (
    e: React.ChangeEvent<HTMLInputElement> | SyntheticEvent<Element, Event> | Date,
    source: string,
    value?: string | object | null,
  ) => {
    if (!value) {
      setFilterPaid('');
    } else {
      const localValue = value as { name: string, id: string };
      setFilterPaid(localValue?.id ?? '');
    }
  };

  const handleLocationFilterChange = (
    e: React.ChangeEvent<HTMLInputElement> | SyntheticEvent<Element, Event> | Date,
    source: string,
    value?: string | object | null,
  ) => {
    if (!value) {
      setFilterLocation('');
    } else {
      const localValue = value as { name: string, id: string };
      setFilterLocation(localValue?.id ?? '');
    }
  };

  const paidStatusOptions = [
    { id: '', name: 'Todas' },
    { id: 'paid', name: 'Pagados' },
    { id: 'pending', name: 'Pendientes' },
  ];

  const locationSummary = [
    {
      title: 'Escuela Militar',
      paid: summaryData.paidEscuelaMilitar,
      total: summaryData.escuelaMilitar,
    },
    {
      title: 'Los Dominicos',
      paid: summaryData.paidLosDominicos,
      total: summaryData.losDominicos,
    },
    {
      title: 'La Dehesa',
      paid: summaryData.paidLaDehesa,
      total: summaryData.laDehesa,
    },
    {
      title: 'Egresados Escuela Militar',
      paid: summaryData.paidEgresadosEscuelaMilitar,
      total: summaryData.egresadosEscuelaMilitar,
    },
  ];

  return (
    <>
      <Box display='flex' alignItems='center'>
        <Typography className={classes.title}>
          <PaymentsIcon fontSize="large" />
          {title}
        </Typography>
      </Box>

      <Divider className={classes.divider} />

      {account?.user?.userType !== SECRETARY && (
        <Box>
          <Box>
            <Box className={classes.indicatorsTotalsContainer}>
              <Box>
                <Typography className={classes.totalLabel}>
                  Total
                </Typography>
                <Typography className={classes.totalValue}>
                  {formatCcy(summaryData.total, '.')}
                </Typography>
                <Typography className={classes.totalDescription}>
                  Total de matrículas/aranceles generados
                </Typography>
              </Box>
              <Box>
                <Typography className={classes.totalLabel}>
                  Total pagado
                </Typography>
                <Typography className={classes.totalValue}>
                  {formatCcy(summaryData.paidTotal, '.')}
                </Typography>
                <Typography className={classes.totalDescription}>
                  Total de pagos por matrículas/aranceles
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={2} className={classes.locationGrid}>
              {locationSummary.map((location) => (
                <Grid item xs={12} sm={6} md={3} key={location.title}>
                  <Paper elevation={0} className={classes.locationCard}>
                    <Typography className={classes.totalLabel}>
                      {location.title}
                    </Typography>
                    <Box className={classes.locationRow}>
                      <Box className={classes.legend}>
                        <span className={`${classes.legendDot} ${classes.legendDotPaid}`} />
                        <Typography className={classes.legendLabel}>
                          Total
                        </Typography>
                      </Box>
                      <Typography className={classes.locationAmount}>
                        {formatCcy(location.total, '.')}
                      </Typography>
                    </Box>
                    <Box className={classes.locationRow}>
                      <Box className={classes.legend}>
                        <span className={`${classes.legendDot} ${classes.legendDotTotal}`} />
                        <Typography className={classes.legendLabel}>
                          Total pagado
                        </Typography>
                      </Box>
                      <Typography className={classes.locationAmount}>
                        {formatCcy(location.paid, '.')}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Divider className={classes.divider} />
        </Box>
      )}

      <Box sx={{ marginTop: 'clamp(10px, 1.25vw, 24px)' }}>
        <Header
          title="Información por alumno"
          buttons={[]}
          search={{
            text: 'Buscar',
            value: searchValue,
            onChangeSearchValue: handleSearchChange,
          }}
          autocompletes={[
            {
              name: 'Estado',
              keyField: 'filterPaid',
              value: filterPaid,
              onChange: handlePaidFilterChange,
              options: paidStatusOptions,
              valueToRetrieveFromSelect: 'id',
              size: 'small',
            },
            {
              name: 'Ubicación',
              keyField: 'filterLocation',
              value: filterLocation,
              onChange: handleLocationFilterChange,
              options: locationsInfo,
              valueToRetrieveFromSelect: 'id',
              size: 'small',
            },
          ]}
        />
      </Box>

      <Box mt={3}>
        {allContracts.totalItems !== 0
          && <CustomTable
            headers={CONTRACT_PAYMENTS_UI.filter((elem) => elem.showOnTable)}
            data={{ values: allContracts.items, count: allContracts.totalItems }}
            userType={(account && account.user && account.user.userType) ?? undefined}
            tableParameters={tableParameters}
            setTableParameters={setTableParameters}
          />}
        {allContracts.totalItems === 0 && <EmptyTable />}
      </Box>
    </>
  );
}

export default BaseContractPaymentListView;
